import { Edit, ImageNotSupportedRounded, LocalOffer, MenuBook, Schedule } from "@mui/icons-material";
import { Box, Button, Card, CardMedia, IconButton, Rating, Stack, Typography } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import { useStore } from "../../../store/useStore";
import { tokens } from "../../../store/tokens";
import "./ServiceCardItem.css";

const DEFAULT_IMAGE = "//aps.songjukti.com/image/web_admin/data_entry.jpg";

/**
 * Validates a (possibly malformed) image URL and returns a safe, well-formed
 * URL, or null if the input is empty/unusable.
 */
const getSafeImageUrl = (rawUrl) => {
  if (!rawUrl || typeof rawUrl !== "string") return null;
  let url = rawUrl.trim();
  if (!url) return null;

  if (url.startsWith("//")) {
    url = "https:" + url;
  } else if (!/^https?:\/\//i.test(url)) {
    url = "https://" + url.replace(/^\/+/, "");
  }

  const match = url.match(/^(https?:\/\/)(.*)$/i);
  if (!match) return null;
  const [, protocol, rest] = match;
  const finalUrl = protocol + rest.replace(/\/{2,}/g, "/");

  try {
    // eslint-disable-next-line no-new
    new URL(finalUrl);
    return finalUrl;
  } catch (e) {
    return null;
  }
};

const pickText = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value[0]?.title ?? "";
  if (typeof value === "object") return value.title ?? "";
  return String(value);
};

const buildMainImageUrl = (service_pic) => {
  if (typeof service_pic === "string" && service_pic.trim()) return service_pic;
  if (Array.isArray(service_pic) && service_pic[0]?.pic_url) {
    return "//yeapbe.com/" + service_pic[0].pic_url;
  }
  return DEFAULT_IMAGE;
};

const extractExtraPicUrl = (entry) => {
  if (!entry) return null;
  if (typeof entry === "string") return entry;
  return entry.mpic || entry.pic_url || entry.service_pic || entry.image || null;
};

// Previously the "0/1/empty means Negotiate" rule and the "which currency
// suffix to show" rule were two separate ternaries that didn't agree —
// price "0" fell through to showing a currency next to "Negotiate", but
// price "" or "1" didn't. One function, one rule.
const formatPrice = (price, currency) => {
  const isNegotiable = price == null || price === "" || price === "0" || price === 0 || price === "1" || price === 1;
  if (isNegotiable) return "Negotiate";
  return `${currency || "BDT"} ${price}`;
};

const NoImageBanner = ({ height = "35vh" }) => (
  <Box
    sx={{
      height,
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: tokens.color.surfaceMuted,
      color: tokens.color.inkFaint,
      gap: 0.5,
    }}
  >
    <ImageNotSupportedRounded sx={{ fontSize: "1.75rem" }} />
    <Typography variant="caption" sx={{ fontWeight: 600 }}>
      No image
    </Typography>
  </Box>
);

const ServiceCardItemFlat = (props) => {
  const navigate = useNavigate();
  const { user } = useStore();

  const { data, setRender, handleUpServiceOpen, editOff } = props;
  const {
    poster,
    service_category,
    service_duration,
    service_info,
    service_pic,
    service_pic_list,
    service_price,
    service_refer,
    service_review,
    offer_st,
    service_currency,
    service_review_quantity,
    service_subcategory,
    service_title,
  } = data;

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 6000,
  };

  const mainImage = getSafeImageUrl(buildMainImageUrl(service_pic));
  const extraImages =
    service_pic_list?.map((element) => getSafeImageUrl(extractExtraPicUrl(element))).filter(Boolean) || [];
  const hasMultipleImages = extraImages.length > 1;
  const allImages = hasMultipleImages ? [mainImage, ...extraImages].filter(Boolean) : [mainImage].filter(Boolean);

  const isOwner = Boolean(user?.sopnoid) && user.sopnoid === poster;
  const rating = typeof service_review === "object" ? Number(service_review?.average_rating) || 0 : Number(service_review) || 0;
  const reviewCount = typeof service_review === "object" ? service_review?.total_review : service_review_quantity;

  const goToDetails = () => navigate("/servicedetails/" + service_refer);

  return (
    <Card
      elevation={0}
      sx={{
        width: "100%",
        borderRadius: `${tokens.card.radius}px`,
        border: `1px solid ${tokens.color.line}`,
        overflow: "hidden",
        transition: "box-shadow 0.15s ease",
        "&:hover": { boxShadow: tokens.shadow.md },
      }}
    >
      <Stack direction={"row"} spacing={2} sx={{ p: 1.5 }}>
        <Box sx={{ width: "30%", position: "relative", borderRadius: `${tokens.radius.md}px`, overflow: "hidden" }}>
          {allImages.length === 0 ? (
            <Box onClick={goToDetails} sx={{ cursor: "pointer" }}>
              <NoImageBanner height="30vh" />
            </Box>
          ) : hasMultipleImages ? (
            <Slider {...sliderSettings}>
              {allImages.map((imgUrl, index) => (
                <Box key={imgUrl + index} sx={{ height: "30vh" }}>
                  <CardMedia component="img" height="100%" image={imgUrl} alt={service_title} sx={{ cursor: "pointer" }} onClick={goToDetails} />
                </Box>
              ))}
            </Slider>
          ) : (
            <Box sx={{ height: "30vh" }}>
              <CardMedia component="img" height="100%" image={allImages[0]} alt={service_title} sx={{ cursor: "pointer" }} onClick={goToDetails} />
            </Box>
          )}
          <Box sx={{ position: "absolute", top: 10, left: 10, px: 1, py: 0.25, borderRadius: `${tokens.radius.sm}px`, bgcolor: "rgba(18,36,46,0.75)" }}>
            <Typography variant="caption" sx={{ color: tokens.color.white, fontWeight: 700 }}>
              Featured
            </Typography>
          </Box>
        </Box>

        <Stack spacing={1} sx={{ flex: 1, cursor: "pointer" }}>
          <Stack direction={"row"} spacing={0.75} alignItems={"center"}>
            <LocalOffer sx={{ fontSize: "1rem", color: tokens.color.teal }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.color.teal }}>
              {pickText(service_category)}
              {pickText(service_subcategory) && ` · ${pickText(service_subcategory)}`}
            </Typography>
          </Stack>

          <Typography variant="subtitle1" sx={{ color: tokens.color.ink }} onClick={goToDetails}>
            {service_title}
          </Typography>

          <Stack direction={"row"} spacing={0.75} alignItems={"center"}>
            <Schedule sx={{ fontSize: "1rem", color: tokens.color.inkFaint }} />
            <Typography variant="body2" sx={{ color: tokens.color.inkMuted }}>
              {pickText(service_duration)}
            </Typography>
          </Stack>

          {service_info && (
            <Stack direction={"row"} spacing={0.75} alignItems={"flex-start"}>
              <MenuBook sx={{ fontSize: "1rem", color: tokens.color.inkFaint, mt: "3px" }} />
              <Typography variant="body2" sx={{ color: tokens.color.inkMuted }} onClick={goToDetails}>
                {service_info}
              </Typography>
            </Stack>
          )}

          <Stack direction={"row"} spacing={0.5} alignItems={"center"}>
            <Rating name="read-only" value={rating} precision={0.5} readOnly size="small" />
            <Typography variant="caption" sx={{ color: tokens.color.inkMuted }}>
              {rating} ({reviewCount ?? 0} reviews)
            </Typography>
          </Stack>
        </Stack>

        <Stack spacing={1} alignItems={"center"} justifyContent={"center"} sx={{ width: "18%", borderLeft: `1px solid ${tokens.color.line}` }}>
          {!editOff && isOwner && (
            <IconButton aria-label="edit" size="small" onClick={() => handleUpServiceOpen(service_refer, offer_st)}>
              <Edit fontSize="small" sx={{ color: tokens.color.inkMuted }} />
            </IconButton>
          )}
          <Typography variant="subtitle1" sx={{ color: tokens.color.ink }}>
            {formatPrice(service_price, service_currency)}
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate("/book/" + service_refer)}
            sx={{
              borderRadius: `${tokens.radius.pill}px`,
              bgcolor: tokens.color.teal,
              textTransform: "none",
              "&:hover": { bgcolor: tokens.color.tealDark },
            }}
          >
            Book now
          </Button>
        </Stack>
      </Stack>
    </Card>
  );
};

export default ServiceCardItemFlat;
